import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'

import { PageHeader } from '@/components/shared'
import { Button } from '@/components/ui/button'
import { Check, X } from 'lucide-react'
import { blogService } from '@/services/blogService'
import { BlogPostForm, slugify } from '@/components/AdminPanel/Blog/BlogPostForm'

const emptyDraft = {
  id: '',
  title: '',
  excerpt: '',
  description: '',
  category: '',
  published_date: dayjs().format('YYYY-MM-DD'),
  read_time: 0,
  image: '',
  image_alt: '',
  author_id: null,
  published: true,
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  canonical_url: '',
  content_blocks: [],
}

const BlogEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = Boolean(id)

  const [draft, setDraft] = useState(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [initialized, setInitialized] = useState(!isEditing)

  const { data, isLoading } = useQuery(['blog', id], () => blogService.getBlogById(id), {
    enabled: isEditing,
    onError: (err) => toast.error('Failed to load blog post: ' + (err.response?.data?.error || err.message))
  })

  useEffect(() => {
    if (!isEditing || !data?.data?.post || initialized) return
    const post = data.data.post
    setDraft({
      id: post.id,
      title: post.title || '',
      excerpt: post.excerpt || '',
      description: post.description || '',
      category: post.category || '',
      published_date: post.published_date || dayjs().format('YYYY-MM-DD'),
      read_time: post.read_time || 0,
      image: post.image || '',
      image_alt: post.image_alt || '',
      author_id: post.author_id || null,
      published: post.published,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      canonical_url: post.canonical_url || '',
      content_blocks: post.content_blocks || [],
    })
    setInitialized(true)
  }, [data, isEditing, initialized])

  async function handleUpload(file) {
    setUploading(true)
    try {
      const response = await blogService.uploadImage(draft.id || 'temp', file)
      return response.data?.url || ''
    } catch (err) {
      toast.error('Failed to upload image: ' + (err.response?.data?.error || err.message))
      return ''
    } finally {
      setUploading(false)
    }
  }

  async function handleSubmit() {
    if (!draft.id || !draft.title) {
      toast.error('Slug and title are required')
      return
    }

    const payload = { ...draft, id: slugify(draft.id) }
    if (!payload.id) {
      toast.error('Slug and title are required')
      return
    }

    setSaving(true)
    try {
      if (isEditing) {
        await blogService.updateBlog(id, payload)
      } else {
        await blogService.createBlog(payload)
      }
      toast.success('Blog post saved')
      queryClient.invalidateQueries('blogs')
      if (isEditing) queryClient.invalidateQueries(['blog', id])
      navigate('/admin-panel')
    } catch (err) {
      toast.error('Failed to save blog post: ' + (err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const formId = 'blog-post-editor-form'

  if (isEditing && (isLoading || !initialized)) {
    return <p className="text-sm text-muted-foreground">Loading blog post...</p>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Edit post' : 'New post'}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={draft.published}
                onChange={(event) => setDraft((current) => ({ ...current, published: event.target.checked }))}
              />
              Published
            </label>
            <Button type="submit" form={formId} disabled={saving || uploading}>
              <Check className="mr-1.5 h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin-panel')}>
              <X className="mr-1.5 h-4 w-4" /> Cancel
            </Button>
          </div>
        }
      />

      <BlogPostForm
        formId={formId}
        draft={draft}
        setDraft={setDraft}
        uploading={uploading}
        onUpload={handleUpload}
        onSubmit={handleSubmit}
        isEditing={isEditing}
      />
    </div>
  )
}

export default BlogEditor
